import React, { useEffect, useState } from 'react';
import './AdminUserProfile.css';
import privateAxios from '../../utils/axios/privateAxios';
import { FaUserCircle, FaEdit, FaUpload } from 'react-icons/fa';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Notification from '../../components/Notification/Notification';

const AdminUserProfile = () => {
    const navigate = useNavigate();
    const {id} = useParams();

    const [userData, setUserData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ type: '', message: '' });

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => {
            setNotification({ type: '', message: '' });
        }, 5000);
    };

    useEffect(() => {
        if (!id) return;

        const fetchUser = async () => {
            try {
                const { data } = await privateAxios.get(`/private/admin-user/${id}`);
                setUserData(data.user);
                setImagePreview(data.user.imageUrl || '');
            } catch (err) {
                console.error('Failed to fetch user', err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    navigate('/unauthorized');
                } else {
                    setError("An error occurred while loading user's profile.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id, navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = async () => {
        if (!selectedImage) return;
        try {
            setUploading(true);
            const fileName = `${id}_${Date.now()}_${selectedImage.name}`;
            const { data: { url } } = await privateAxios.get(`/private/generate-upload-url`, {
                params: { filename: fileName }
            });


            await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': selectedImage.type },
                body: selectedImage,
            });

            const newImageUrl = url.split('?')[0];

            await privateAxios.put(`/private/edit-photo/${id}`, { newImageUrl });
            setUserData((prev) => ({ ...prev, newImageUrl }));
            setImagePreview(newImageUrl);
            setSelectedImage(null);
            showNotification('success', "User's profile image uploaded successfully!")
        } catch (err) {
            console.error('Image upload failed', err);
            showNotification('error', "User's profile image update failed!")
        } finally {
            setUploading(false);
        }
    };

    if (loading) return <div className="loading">Loading profile...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!userData) return null;

    return (
        <div className="user-profile">
            <Notification
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification({ type: '', message: '' })}
            />
            <div className="user-profile-wrapper">
                <h2>My Profile</h2>

                    <button className="admin-user-profile-back-to-users-btn" onClick={() => navigate('/admin-users')}>
                        Back to Users
                    </button>

                <div className="admin-user-profile-profile-image-section">
                    {imagePreview ? (
                        <img src={imagePreview} alt="Profile" className="profile-image" />
                    ) : (
                        <FaUserCircle className="default-icon" />
                    )}
                    <label className="admin-user-profile-edit-image-icon">
                        <FaEdit />
                        <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </label>
                    {selectedImage && (
                        <button className="admin-user-profile-upload-btn" onClick={handleImageUpload} disabled={uploading}>
                            {uploading ? 'Uploading...' : <><FaUpload /> Upload</>}
                        </button>
                    )}
                </div>

                <div className="admin-user-profile-fields">
                    <ProfileRow label="Username" value={userData.username} />
                    <ProfileRow label="First Name" value={userData.firstname} />
                    <ProfileRow label="Last Name" value={userData.lastname} />
                    <ProfileRow label="Email" value={userData.email} />
                    <ProfileRow label="Phone Number" value={userData.phoneNumber} />
                    <ProfileRow label="Gender" value={userData.gender} />
                    <ProfileRow label="Date of Birth" value={userData.dateOfBirth?.substring(0, 10)} />
                    <ProfileRow label="Bio" value={userData.bio} />
                    <ProfileRow label="Status" value={userData.status} />
                    <ProfileRow label="Verified" value={String(userData.verified)} />
                    <ProfileRow label="Education Level" value={userData.educationLevel} />
                    <ProfileRow label="Occupation" value={userData.occupation} />
                    <ProfileRow label="Village" value={userData.village} />
                    <ProfileRow label="Town" value={userData.town} />
                    <ProfileRow label="State of Residence" value={userData.stateOfResidence} />
                </div>


                <Link to={`/admin-edit-user/${userData._id}`} className="admin-user-profile-edit-profile-link">
                    Edit User Profile
                </Link>
            </div>
        </div>
    );
};

// Simple field row component
const ProfileRow = ({ label, value }) => (
    <div className="admin-user-profile-field-row">
        <label>{label}:</label>
        <span>{value || 'N/A'}</span>
    </div>
);

export default AdminUserProfile;